import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { deleteWaterStation, getStation, updateWaterStation } from '~/features/stations/service'
import { UpdateStationForm } from '~/features/stations/components/config/StationForm'
import { getSite } from '~/features/sites/service'
import { Button, Callout, Flex, Separator, Text } from '@radix-ui/themes'
import { SectionCard } from '~/components/SectionCard'
import { listDataSources } from '~/features/stations/datasources-service'
import { EmptyState } from '~/components/EmptyState'
import { Fragment, useState } from 'react'
import { MeterForm } from '~/features/stations/components/config/MeterForm'
import { MdOutlineWifiTethering } from 'react-icons/md'
import { LuArchiveRestore } from 'react-icons/lu'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { ConsumptionAndLeaksSection } from '~/features/stations/components/ConsumptionAndLeakSection'
import LeakTriggerConfigSection from '~/features/stations/components/config/LeakTriggerConfigSection'
import { MeterSourcePreview } from '~/features/stations/components/config/MeterSourcePreview'
import { BudgetGoalList } from '~/features/budget/components/BudgetGoalList'
import {
  CreateManualDataSourceForm,
  UpdateManualDataSourceForm
} from '~/features/stations/components/config/ManualDataSourceForm'

const EditStationPage = () => {
  const { t } = useTranslation('sites')
  const { siteID, stationID } = Route.useParams()
  const { queryClient, station } = Route.useRouteContext()
  const [isAddingMeter, setIsAddingMeter] = useState(false)
  const [isAddingManualDatasource, setIsAddingManualDatasource] = useState(false)
  const navigate = useNavigate()

  const { data: site } = useSuspenseQuery({
    queryKey: ['site', siteID],
    queryFn: () => getSite({ siteId: siteID })
  })

  const { data: datasources = [] } = useQuery({
    queryKey: ['station-datasources', stationID],
    queryFn: () => listDataSources({ stationID })
  })

  const { mutate: updateStation, isPending } = useMutation({
    mutationFn: updateWaterStation,
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          const isStationsOrSite = ['stations', 'site'].includes(`${queryKey[0]}`) && queryKey[1] === siteID
          const isStation = queryKey[0] === 'station' && queryKey[1] === stationID
          return isStationsOrSite || isStation
        }
      })
      toast.success(t('stations.form.messages.updateSuccess'))
    },
    onError: (error) => {
      console.error('Error updating station', error)
      toast.error(t('stations.form.messages.updateError'))
    }
  })

  const { mutate: deleteStation, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteWaterStation({ stationID }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['stations', siteID]
      })
      navigate({ to: '/sites/$siteID/config/stations', params: { siteID } })
      toast.success(t('stations.form.messages.deleteSuccess'))
    },
    onError: (error) => {
      console.error('Error deleting station', error)
      toast.error(t('stations.form.messages.deleteError'))
    }
  })

  const handleDeleteClick = () => {
    if (window.confirm(t('stations.dangerZone.confirmDelete'))) {
      deleteStation()
    }
  }

  const isAddingSource = isAddingMeter || isAddingManualDatasource

  return (
    <Flex direction="column" gap="4">
      <UpdateStationForm station={station} onSubmit={updateStation} isPending={isPending} site={site} />
      <SectionCard
        title={t('stations.datasourceSection.title')}
        secondaryActions={
          datasources.length > 0 && (
            <Flex gap={'2'}>
              {!datasources.some((ds) => ds.sourceType === 'meter') && (
                <Button
                  size={'2'}
                  variant={'soft'}
                  disabled={isAddingMeter}
                  onClick={() => setIsAddingMeter(true)}
                >
                  <MdOutlineWifiTethering />
                  {t('stations.meter.buttons.assignMeter')}
                </Button>
              )}
              {!datasources.some((ds) => ds.sourceType === 'manual') && (
                <Button
                  size={'2'}
                  variant={'soft'}
                  disabled={isAddingManualDatasource}
                  onClick={() => setIsAddingManualDatasource(true)}
                >
                  <LuArchiveRestore />
                  {t('stations.meter.buttons.importData')}
                </Button>
              )}
            </Flex>
          )
        }
      >
        <Text size="2" color="gray">
          {t('stations.datasourceSection.description')}
        </Text>
        {isAddingMeter ? (
          <MeterForm station={station} onCancel={() => setIsAddingMeter(false)} />
        ) : isAddingManualDatasource ? (
          <CreateManualDataSourceForm
            stationID={stationID}
            onCancel={() => setIsAddingManualDatasource(false)}
            onSuccess={() => setIsAddingManualDatasource(false)}
          />
        ) : null}
        {isAddingSource && datasources.length > 0 && <Separator size={'4'} mt={'4'} />}
        {datasources.map((ds, idx) => (
          <Fragment key={ds.id}>
            {idx > 0 && <Separator size={'4'} mt={'4'} />}
            {ds.sourceType === 'meter' ? (
              <MeterSourcePreview meter={ds} />
            ) : (
              <UpdateManualDataSourceForm source={ds} stationID={stationID} />
            )}
          </Fragment>
        ))}
        {!isAddingSource && datasources.length === 0 && (
          <EmptyState
            title={t('stations.meter.emptyState.title')}
            description={t('stations.meter.emptyState.description')}
            className="h-64"
            afterBody={
              <Flex gap={'4'}>
                <Button size={'2'} variant={'soft'} onClick={() => setIsAddingMeter(true)}>
                  <MdOutlineWifiTethering />
                  {t('stations.meter.buttons.assignMeter')}
                </Button>
                <Button size={'2'} variant={'soft'} onClick={() => setIsAddingManualDatasource(true)}>
                  <LuArchiveRestore />
                  {t('stations.meter.buttons.importData')}
                </Button>
              </Flex>
            }
          />
        )}
      </SectionCard>

      <LeakTriggerConfigSection station={station} datasources={datasources} />
      <ConsumptionAndLeaksSection station={station} />
      <SectionCard title={t('navigation.budgetPlanning')}>
        <Flex direction={'column'} gap="6">
          <Text size="2" color="gray">
            {t('stations.objectives.description')}
          </Text>
          <BudgetGoalList siteID={siteID} stationID={stationID} />
        </Flex>
      </SectionCard>

      <SectionCard title={t('stations.dangerZone.title')}>
        <Callout.Root color="red" variant="surface" className="flex items-center justify-between">
          <Callout.Text>{t('stations.dangerZone.deleteWarning')}</Callout.Text>
          <Button variant="soft" onClick={handleDeleteClick} disabled={isDeleting}>
            {t('stations.dangerZone.removeStation')}
          </Button>
        </Callout.Root>
      </SectionCard>
    </Flex>
  )
}

export const Route = createFileRoute('/sites/$siteID/config/stations/$stationID')({
  beforeLoad: async ({ params, context }) => {
    const station = await getStation({ stationID: params.stationID })
    return {
      ...context,
      station
    }
  },
  component: EditStationPage
})
